import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Constants } from "../../constants";
import { environment } from "../../environments/environment";
import { Company } from "../../models/company";
import * as moment from 'moment';
import { Service } from "../../models/service";
import { CompanyDocument } from "../../models/CompanyDocument";
import { firstValueFrom } from "rxjs";

export interface PosNexiTerminal {
    deviceId: string;
    name: string;
    storeName: string;
    externalTerminalId: string;
    serialNumber: string;
    status: string;
}

@Injectable({
    providedIn: 'root'
})
export class CompanyService {
    private approveInFlight = new Set<string>();

    constructor(private http: HttpClient) {
    }

    getRequestHeaders() {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': localStorage.getItem(Constants.LOCAL_TOKEN) ?? environment.publicApiToken
        });
    }

    getPublicRequestHeaders() {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': environment.publicApiToken,
        });
    }

    getFormDataRequestHeaders() {
        return new HttpHeaders({
            'Authorization': localStorage.getItem(Constants.LOCAL_TOKEN) ?? environment.publicApiToken
        });
    }

    getPublicFormDataRequestHeaders() {
        return new HttpHeaders({
            'Authorization': environment.publicApiToken
        });
    }

    async register(company: Company, billingPeriod: 'monthly' | 'yearly') {
        console.log('register company: ', company)

        let companyToRegister =
        {
            "firstName": company.legalFirstName,
            "lastName": company.legalLastName,
            "email": (company.legalEmail ?? "").trim().toLowerCase(),
            "phone": company.legalPhone,
            "password": company.legalPassword,
            "address": company.legalAddress,
            "city": company.legalCity,
            "zip": company.legalZip,
            "province": company.legalProvince,
            "fiscalCode": company.legalFiscalCode,
            "documentNumber": company.legalDocumentNumber,
            "documentFront": company.legalDocumentFront,
            "documentBack": company.legalDocumentBack,
            "fiscalCodeFront": company.legalFiscalCodeFront,
            "fiscalCodeBack": company.legalFiscalCodeBack,
            "acceptTerms": new Date().toISOString(),
            "acceptPrivacy": new Date().toISOString(),
            "company": {
                "name": company.name,
                "legalFirstName": company.legalFirstName,
                "legalLastName": company.legalLastName,
                "legalFiscalCode": company.legalFiscalCode,
                "legalEmail": company.legalEmail,
                "legalPhone": company.legalPhone,
                "legalAddress": company.legalAddress,
                "legalZip": company.legalZip,
                "legalCity": company.legalCity,
                "legalProvince": company.legalProvince,
                "fiscalCode": company.fiscalCode,
                "vat": company.vat,
                "pec": company.pec,
                "sdi": company.sdi,
                "address": company.address,
                "addressNumber": company.addressNumber != null && String(company.addressNumber).trim() !== ''
                    ? String(company.addressNumber).trim()
                    : undefined,
                "city": company.city,
                "zip": company.zip,
                "province": company.province,
                "companyEmail": company.companyEmail,
                "iban": company.iban,
                "federation": company.federation,
                "assignedCode": company.assignedCode,
                "acceptTerms": new Date().toISOString(),
                "acceptPrivacy": new Date().toISOString(),
                "acceptContract": new Date().toISOString(),
                "acceptRenew": new Date().toISOString(),
                "service": company.service.id,
                "billedPeriod": billingPeriod
            },
        };

        // console.log('company to register: ', companyToRegister)
        try {
            let response: any = await this.http.post(environment.authApiUrl + '/signupCompany', companyToRegister, {
                headers: this.getPublicRequestHeaders(),
            }).toPromise();

            console.log('response createCompany: ', response)

            return response;

        } catch (e: any) {
            console.error('error creating user: ', e);
            return e?.error ?? { success: false, code: 'UNKNOWN_ERROR' };
        }
    }


    async checkOTP(otp: string, email: string) {
        try {
            return await this.http.post(environment.authApiUrl + '/verify', { otp, email }, { headers: this.getPublicRequestHeaders() }).toPromise();
        } catch (e: any) {
            console.error('error checkOTP: ', e);
            return e?.error ?? { success: false, code: 'OTP_NOT_VALID' };
        }
    }

    async getCompaniesPending() {
        let companies: Company[] = [];
        let response: any;

        try {
            response = await this.http.get(environment.apiUrl + '/companies?includePendingApprovation=1', {
                headers: this.getRequestHeaders()
            }).toPromise();

            if (response.success) {
                companies = response.data
                    .map((company: Company) => {
                        return { ...company, boardingDate: moment(company.createdAt).format('DD-MM-YYYY') }
                    });
            }
        } catch (e) {
            console.error('error getting pendingCompanies: ', e)
        } finally {
            return companies;
        }
    }

    async getCompanies(params?: any) {
        let response: any;

        try {
            response = await this.http.get(environment.apiUrl + `/companies`, {
                params: params,
                headers: this.getRequestHeaders()
            }).toPromise();

            return response.data;

        } catch (e) {
            console.error('error getCompanies: ', e)
        } finally {
            return response.data;
        }
    }

    async getCompaniesPage(params?: any): Promise<{ success?: boolean; data?: any[]; count?: number }> {
        try {
            return await this.http.get<{ success?: boolean; data?: any[]; count?: number }>(
                environment.apiUrl + '/companies',
                { params, headers: this.getRequestHeaders() }
            ).toPromise() as { success?: boolean; data?: any[]; count?: number };
        } catch (e) {
            console.error('error getCompaniesPage: ', e);
            return { success: false, data: [], count: 0 };
        }
    }

    async getCompanyById(companyId: string) {
        let response: any;

        try {
            response = await this.http.get(environment.publicApiUrl + `/companies/${companyId}`, { headers: this.getPublicRequestHeaders() }).toPromise();

            return response;

        } catch (e) {
            console.error('error getCompanyById: ', e)
        } finally {
            return response;
        }
    }

    async getCompanyByIdAuthenticated(companyId: string) {
        try {
            return await this.http.get(environment.apiUrl + `/companies/${companyId}`, {
                headers: this.getRequestHeaders(),
            }).toPromise();
        } catch (e) {
            console.error('error getCompanyByIdAuthenticated: ', e);
            return null;
        }
    }

    async getCompanyFromDomain(domain: string) {
        let response: any = null;
        let params = { domain: domain };

        try {
            response = await this.http.get(environment.publicApiUrl + '/companies', {
                headers: this.getPublicRequestHeaders(),
                params,
            }).toPromise();

            return response.data[0];

        } catch (e) {
            console.error('error getCompanyIdFromDomain: ', e)
        }
    }

    async getCompanyFromShopDomain(domain: string): Promise<Company | null> {
        const params = { shopDomain: domain };
        try {
            const response: any = await firstValueFrom(this.http.get(environment.publicApiUrl + '/companies', {
                headers: this.getPublicRequestHeaders(),
                params,
            }));

            if (response.data?.length > 0) {
                localStorage.setItem('wisper_companyId', response.data[0].id)
                return new Company(response.data[0]);
            }

            return null;
        } catch (e) {
            console.error('error getCompanyIdFromDomain: ', e)
            return null;
        }
    }

    async updateCompany(id: string, updatedCompany: any) {
        let response: any;
        try {
            response = await this.http.put(environment.apiUrl + `/companies/${id}`, updatedCompany, { headers: this.getRequestHeaders() }).toPromise();

        } catch (e) {
            console.error('error updateCompany: ', e)
        } finally {
            return response
        }
    }

    async getPosNexiTerminals(companyId: string, businessId: string): Promise<{success: boolean; data: PosNexiTerminal[]}> {
        try {
            const response: any = await this.http.get(
                environment.apiUrl + `/companies/${companyId}/pos-nexi-terminals`,
                {headers: this.getRequestHeaders(), params: {businessId}}
            ).toPromise();
            return {success: !!response?.success, data: Array.isArray(response?.data) ? response.data : []};
        } catch (e) {
            console.error('error getPosNexiTerminals: ', e);
            return {success: false, data: []};
        }
    }

    async publicUpdateCompany(updatedCompany: Company) {
        let response: any;
        try {
            response = await this.http.put(environment.publicApiUrl + `/companies/${updatedCompany.id}`, updatedCompany, { headers: this.getPublicRequestHeaders() }).toPromise();

        } catch (e) {
            console.error('error updateCompany: ', e)
        } finally {
            return response
        }
    }

    async uploadCompany(formData: FormData) {
        let responseUpload: any;

        try {
            responseUpload = await this.http.post(environment.apiUrl + `/companies/upload`, formData, {
                headers: this.getFormDataRequestHeaders(),
            }).toPromise();

            return responseUpload

        } catch (e) {
            console.error('uploadCompany: ', e)
        }
    }

    async uploadCompanyPublic(formData: FormData) {
        let responseUpload: any;

        try {
            responseUpload = await this.http.post(environment.publicApiUrl + `/companies/upload`, formData, {
                headers: this.getPublicFormDataRequestHeaders(),
            }).toPromise();

            return responseUpload

        } catch (e) {
            console.error('uploadCompanyPublic: ', e)
        }
    }

    async enableDisablePaymentMethod(paymentMethod: any, companyId: string) {
        try {
            let result: any = await this.http.put(environment.apiUrl + `/companies/${companyId}`, paymentMethod, { headers: this.getRequestHeaders() }).toPromise();
            return result.success;
        } catch (e) {
            console.error('error enableDisablePaymentMethod: ', e);
        }
    }

    async approveCompany(companyId: string) {
        if (this.approveInFlight.has(companyId)) {
            return { success: false, error: 'APPROVE_IN_PROGRESS' };
        }

        this.approveInFlight.add(companyId);
        let response: any;
        console.log('company service approveCompany')
        try {
            response = await this.http.post(environment.apiUrl + `/companies/${companyId}/approve`, {}, { headers: this.getRequestHeaders() }).toPromise();
        } catch (e) {
            console.error('error approveCompany: ', e)
        } finally {
            this.approveInFlight.delete(companyId);
            return response;
        }
    }

    async sendApprovalEmail(companyId: string) {
        let response: any;
        console.log('company service sendApprovalEmail')
        try {
            response = await this.http.get(environment.apiUrl + `/companies/${companyId}/sendApprovalEmail`, {
                headers: this.getRequestHeaders()
            }).toPromise();
        } catch (e) {
            console.error('error sendApprovalEmail: ', e)
        } finally {
            return response;
        }
    }

    async refuseCompany(companyId: string) {
        let response: any;

        try {
            response = await this.http.post(environment.apiUrl + `/companies/${companyId}/reject`, {}, { headers: this.getRequestHeaders() }).toPromise();
        } catch (e) {
            console.error('error approveCompany: ', e)
        } finally {
            return response;
        }
    }

    async onBoarding(companyId: string) {
        let response: any;

        try {
            response = await this.http.post(environment.publicApiUrl + `/companies/${companyId!}/onboarding`, {}, { headers: this.getPublicRequestHeaders() }).toPromise();

        } catch (e) {
            console.error('error onBoarding: ', e)
        } finally {
            return response;
        }
    }

    async getCompanyDocuments(companyId: string) {
        let response: any = null;
        let params = { companyId: companyId };
        try {
            response = await this.http.get(environment.apiUrl + '/companyDocuments', {
                headers: this.getRequestHeaders(),
                params,
            }).toPromise();

            return response.data;

        } catch (e) {
            console.error('error getCompanyIdFromDomain: ', e)
        }
    }

    // Product

    async shopGetProducts(companyId: string) {
        let response: any = null;

        try {
            response = await this.http.get(environment.apiUrl + `/companies/${companyId!}/shop`, {
                headers: this.getPublicRequestHeaders(),
            }).toPromise();

            return response.data[0];

        } catch (e) {
            console.error('error getProducts: ', e)
        }
    }

    /*    async shopAddProduct(newProduct: Shop) {
            let response: any;
    
            try {
                response = await this.http.post(environment.apiUrl + '/product', newProduct, {headers: this.getRequestHeaders()}).toPromise();
            } catch (e) {
                console.error('error addProduct: ', e)
            } finally {
                return response;
            }
        }
    
        async shopDeleteProduct(productId: string) {
            let response: any;
    
            try {
                response = await this.http.delete(environment.apiUrl + `/product/${productId}`, {headers: this.getRequestHeaders()}).toPromise();
            } catch (e) {
                console.error('error deleteProduct: ', e)
            } finally {
                return response;
            }
        }
    
        // Sizes
    
        async shopGetSizes(companyId: string){
            let response: any = null;
    
            try {
                response = await this.http.get(environment.apiUrl + `/companies/${companyId!}/size`, {
                    headers: this.getPublicRequestHeaders(),
                }).toPromise();
    
                return response.data[0];
    
            } catch (e) {
                console.error('error getSizes: ', e)
            }
        }
    
        async shopAddSize(newSize: Shop) {
            let response: any;
    
            try {
                response = await this.http.post(environment.apiUrl + '/size', newSize, {headers: this.getRequestHeaders()}).toPromise();
            } catch (e) {
                console.error('error addSize: ', e)
            } finally {
                return response;
            }
        }*/

    async shopDeleteSize(sizeId: string) {
        let response: any;

        try {
            response = await this.http.delete(environment.apiUrl + `/size/${sizeId}`, { headers: this.getRequestHeaders() }).toPromise();
        } catch (e) {
            console.error('error deleteSize: ', e)
        } finally {
            return response;
        }
    }

    // Sizes

    async shopGetColor(companyId: string) {
        let response: any = null;

        try {
            response = await this.http.get(environment.apiUrl + `/companies/${companyId!}/color`, {
                headers: this.getPublicRequestHeaders(),
            }).toPromise();

            return response.data[0];

        } catch (e) {
            console.error('error getColors: ', e)
        }
    }

    /*async shopAddColor(newSize: Shop) {
        let response: any;

        try {
            response = await this.http.post(environment.apiUrl + '/color', newSize, {headers: this.getRequestHeaders()}).toPromise();
        } catch (e) {
            console.error('error addColor: ', e)
        } finally {
            return response;
        }
    }*/

    async shopDeleteColor(sizeId: string) {
        let response: any;

        try {
            response = await this.http.delete(environment.apiUrl + `/color/${sizeId}`, { headers: this.getRequestHeaders() }).toPromise();
        } catch (e) {
            console.error('error deleteColor: ', e)
        } finally {
            return response;
        }
    }
}
